import { type AnyAction, combineReducers } from 'redux';

import type { DashboardVideo } from 'js/src/client';
import {
    addViews,
    clearVideos,
    deleteVideoFromStore,
    deleteVideoFromView,
    removeView,
    setActiveView,
    setBulkActionSpinner,
    setLoadingDashboard,
    setVideoSearchTerm,
    storeLoadedUpToPage,
    storeMoreVideosToLoad,
    storeRenderEstimates,
    storeVideos,
    storeVideosFilterByUserId,
    storeVideosSortBy,
    updateVideoProperty,
    updateViewMetadata,
} from 'js/src/data/dashboard/dashboard-actions';
import { getDefaultDashboardPath, viewIdFromPath } from 'js/src/data/dashboard/dashboard-routes';
import type { RenderEstimateStatus, View } from 'js/src/data/dashboard/dashboard-types';
import {
    ALL_VIDEOS_VIEW_ID,
    INITIAL_VIEWS,
    VIDEOS_KEY,
} from 'js/src/data/dashboard/dashboard-types';
import type { PlainJsStoreOrmData } from 'js/src/data/plain-js-store-orm';
import PlainJsStoreORM from 'js/src/data/plain-js-store-orm';
import type { DeepReadonly } from 'js/src/libs/consts';

export function loadingDashboard(state = true, action: AnyAction) {
    if (storeVideos.match(action)) {
        return false;
    }
    if (setLoadingDashboard.match(action)) {
        return action.payload;
    }
    return state;
}

export function estimatedRenderDurations(
    state = PlainJsStoreORM.createEmpty<DeepReadonly<RenderEstimateStatus>, number>().data,
    action: AnyAction,
) {
    if (storeRenderEstimates.match(action)) {
        // replace old data
        const orm = PlainJsStoreORM.createEmpty<DeepReadonly<RenderEstimateStatus>, number>();
        const videoIdKeys = Object.keys(action.payload).map(Number);
        videoIdKeys.forEach((videoId) => {
            orm.pushItem(
                {
                    videoId,
                    ...action.payload[videoId],
                },
                'videoId',
            );
        });
        return orm.data;
    }
    return state;
}

// Derive the initial view from the URL so the dashboard renders the right
// tab on first paint. Project views aren't in the ORM until projects load
// asynchronously — fall back to the default tab in that case, and the
// route-sync component will switch to the project once it's available.
const getDefaultViewId = (): number => {
    if (typeof window !== 'undefined') {
        const fromPath = viewIdFromPath(window.location.pathname);
        if (fromPath !== null && fromPath < 0) return fromPath;
    }
    return viewIdFromPath(getDefaultDashboardPath()) ?? ALL_VIDEOS_VIEW_ID;
};

export function activeView(state = getDefaultViewId(), action: AnyAction) {
    if (setActiveView.match(action)) {
        return action.payload.view;
    }
    return state;
}

export const _initialViewOrm = (() => {
    const viewOrm = PlainJsStoreORM.createEmpty<Readonly<View>, number>();
    INITIAL_VIEWS.forEach((initialView) => {
        viewOrm.pushItem(initialView, 'id');
    });
    return viewOrm.data;
})();

function updateViewField<Key extends keyof View>(
    viewOrm: PlainJsStoreORM<Readonly<View>, number>,
    viewId: number,
    key: Key,
    value: View[Key],
) {
    viewOrm.updateById(viewId, (view) => (view[key] === value ? view : { ...view, [key]: value }));
}

function updateViewVideos(
    viewOrm: PlainJsStoreORM<Readonly<View>, number>,
    viewId: number,
    updater: (videosOrm: PlainJsStoreORM<DeepReadonly<DashboardVideo>, number>) => void,
) {
    const videos = viewOrm.getByIdOrThrow(viewId)[VIDEOS_KEY];
    const videosOrm = new PlainJsStoreORM(videos);
    updater(videosOrm);
    if (videosOrm.data !== videos) {
        updateViewField(viewOrm, viewId, VIDEOS_KEY, videosOrm.data);
    }
}

function viewIdsContainingVideo(viewOrm: PlainJsStoreORM<Readonly<View>, number>, videoId: number) {
    return viewOrm
        .filter((view) => new PlainJsStoreORM(view[VIDEOS_KEY]).contains(videoId))
        .map((view) => view.id);
}

export function views(
    state: PlainJsStoreOrmData<Readonly<View>, number> = _initialViewOrm,
    action: AnyAction,
) {
    const viewOrm = new PlainJsStoreORM(state);

    /* ADD_VIEWS */
    if (addViews.match(action)) {
        action.payload.views.forEach((view) => {
            viewOrm.pushItem(view, 'id');
        });
        return viewOrm.data;
    }

    /* REMOVE_VIEW */
    if (removeView.match(action)) {
        viewOrm.delete([action.payload.view]);
        return viewOrm.data;
    }

    /* UPDATE_VIEW_METADATA */
    if (updateViewMetadata.match(action)) {
        if (!viewOrm.contains(action.payload.view)) {
            return state;
        }
        if (action.payload.name) {
            updateViewField(viewOrm, action.payload.view, 'name', action.payload.name);
        }
        if (action.payload.color) {
            updateViewField(viewOrm, action.payload.view, 'color', action.payload.color);
        }
        if (action.payload.icon) {
            updateViewField(viewOrm, action.payload.view, 'icon', action.payload.icon);
        }
        return viewOrm.data;
    }

    /* STORE_VIDEOS */
    if (storeVideos.match(action)) {
        updateViewVideos(viewOrm, action.payload.view, (videosOrm) => {
            action.payload.videos.forEach((video) => {
                if (action.payload.addToEnd) {
                    videosOrm.pushItem(video, 'id');
                } else {
                    videosOrm.pushItemToFront(video, 'id');
                }
            });
        });
        return viewOrm.data;
    }

    /* STORE_MORE_VIDEOS_TO_LOAD */
    if (storeMoreVideosToLoad.match(action)) {
        if (!viewOrm.contains(action.payload.view)) {
            return state;
        }
        updateViewField(
            viewOrm,
            action.payload.view,
            'moreVideosToLoad',
            action.payload.hasMoreVideos,
        );
        return viewOrm.data;
    }

    /* STORE_LOADED_PAGE_NUMBER */
    if (storeLoadedUpToPage.match(action)) {
        if (!viewOrm.contains(action.payload.view)) {
            return state;
        }
        updateViewField(
            viewOrm,
            action.payload.view,
            'loadedUpToPage',
            action.payload.loadedUpToPage,
        );
        return viewOrm.data;
    }

    /* CLEAR_VIDEOS */
    if (clearVideos.match(action)) {
        updateViewVideos(viewOrm, action.payload.view, (videosOrm) => {
            videosOrm.delete(videosOrm.allIds);
        });
        updateViewField(viewOrm, action.payload.view, 'loadedUpToPage', 0);
        updateViewField(viewOrm, action.payload.view, 'moreVideosToLoad', true);
        return viewOrm.data;
    }

    /* STORE_VIDEOS_SORT_BY */
    if (storeVideosSortBy.match(action)) {
        if (!viewOrm.contains(action.payload.view)) {
            return state;
        }
        updateViewField(viewOrm, action.payload.view, 'videosSortBy', action.payload.videosSortBy);
        return viewOrm.data;
    }

    /* STORE_VIDEOS_FILTER_BY_USER_ID */
    if (storeVideosFilterByUserId.match(action)) {
        if (!viewOrm.contains(action.payload.view)) {
            return state;
        }
        updateViewField(viewOrm, action.payload.view, 'filterByUserId', action.payload.userId);
        return viewOrm.data;
    }

    /* UPDATE_VIDEO_PROPERTY */
    if (updateVideoProperty.match(action)) {
        const { videoId, property, newValue } = action.payload;
        viewIdsContainingVideo(viewOrm, videoId).forEach((viewId) => {
            updateViewVideos(viewOrm, viewId, (videosOrm) => {
                videosOrm.updateById(videoId, (video) =>
                    video[property] === newValue ? video : { ...video, [property]: newValue },
                );
            });
        });
        return viewOrm.data;
    }

    /* DELETE_VIDEO_FROM_VIEW */
    if (deleteVideoFromView.match(action)) {
        updateViewVideos(viewOrm, action.payload.view, (videosOrm) => {
            videosOrm.delete([action.payload.videoId]);
        });
        return viewOrm.data;
    }

    /* DELETE_VIDEO */
    if (deleteVideoFromStore.match(action)) {
        viewIdsContainingVideo(viewOrm, action.payload.videoId).forEach((viewId) => {
            updateViewVideos(viewOrm, viewId, (videosOrm) => {
                videosOrm.delete([action.payload.videoId]);
            });
        });
        return viewOrm.data;
    }

    return state;
}

export function videoSearchTerm(state = '', action: AnyAction) {
    if (setVideoSearchTerm.match(action)) {
        return action.payload;
    }
    return state;
}

export function bulkActionSpinner(state = false, action: AnyAction) {
    if (setBulkActionSpinner.match(action)) {
        return action.payload;
    }
    return state;
}

export const dashboard = combineReducers({
    activeView,
    views,
    loadingDashboard,
    estimatedRenderDurations,
    videoSearchTerm,
    bulkActionSpinner,
});

export type DashboardState = ReturnType<typeof dashboard>;
